/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import numeral from 'numeral';
import { withStyles } from '@glu/theming';
import { connect } from 'react-redux';
import { withFormContext } from '@glu/validation-react';
import classnames from 'classnames';
import get from 'lodash/get';

// components
import { Button, PRIMARY, SECONDARY } from '@glu/buttons-react';
import withLocale from '../withLocale/withLocale';

// utils
import styles from './createPaymentFooter.styles';
import AppPropTypes from '../../proptypes/AppPropTypes';
import { getPaymentType } from './CreatePayment.utils';

/**
 * Component should NOT has been updated, unless YOU ARE SURE what you are doing.
 * Component is generic and should be used this way, should be extended only if you have to.
 */
export function CreatePaymentFooterComponent({
  action,
  append,
  cancel,
  children,
  classes,
  data,
  disableOnLoad,
  fields,
  isSubmitting,
  locale,
  onChange,
  options,
  preloadFields,
  remainingApprovals,
  setIsSubmitting,
  setLoading,
  showTotal,
  submissionDisabled,
  submit,
  validate
}) {
  const state = preloadFields || fields;
  const paymentType = get(
    state,
    'paymentType[0].type',
    typeof state.paymentType === 'string'
      ? state.paymentType
      : getPaymentType(state, options.paymentTypeOptions)
  );
  const paymentTypeSelected = get(data, 'paymentType', []).length > 0;
  const currency =
    options.fxRates.find(
      (x) => x.id === (get(fields.currency || state.currency, '0.id') || fields.currency)
    ) || {};
  const exchangeRate = Number(data.exchangeRate || 1);
  const amount = data.amount ? numeral(data.amount).value().toFixed(2) : '0.00';
  const amountType = get(data, 'amountType[0].type', data.amountTypeCurrency);
  const currencySuffix = get(currency, 'currency');
  const isForeignCurrency = currencySuffix && currencySuffix !== 'USD' && currencySuffix !== '';
  const convertedAmount =
    amountType === 'D'
      ? // USD Equivalent Calculation
        (amount / exchangeRate).toFixed(2)
      : // Foreign Amount Calculation
        amount;
  let templateAmountCheck = true;
  if (action.includes('template') && amountType === 'D') templateAmountCheck = false;
  // If currency is foreign then show suffix
  const displayAmount =
    isForeignCurrency && templateAmountCheck
      ? `${numeral(convertedAmount).format('0,0.00')} ${get(currency, 'currency', '')}`
      : `$${numeral(amount).format('0,0.00')}`;

  const [numOfApprovalsRequiredForTemplate, setNumOfApprovalsRequiredForTemplate] =
    React.useState(0);

  // Determine whether to show Save/Submit or Submit for Approval on templates
  React.useEffect(() => {
    // If on template modify then run this check to switch out submit button text
    const accountId = get(data.originatorAccountId, '[0].id');
    if (action.includes('template') && data.originatorAccountId) {
      axios
        .post('/payments/getAccountApprovalsCount', { accountId })
        .then(({ data: { data, success } }) => {
          if (success) {
            setNumOfApprovalsRequiredForTemplate(() => data.numOfApprovalsRequired);
            onChange('requiresApproval', data.numOfApprovalsRequired);
          }
        })
        .catch();
    }
  }, [data.originatorAccountId, action, onChange]);

  return (
    <footer className="workflow-footer" data-qa="create-payment-template-footer">
      <div className={classes.footerContainer}>
        <div className={classes.footerTotal}>
          {showTotal && fields.amount && (
            <div className={classes.currentFooterAmount}>
              <span aria-live="polite" className="amount" data-qa={`${paymentType}-total`}>
                {displayAmount}
              </span>
            </div>
          )}
        </div>
        <div className={classes.actionButtons}>
          {paymentTypeSelected && (
            <Button
              variant={PRIMARY}
              type="button"
              disabled={(disableOnLoad && setLoading) || isSubmitting}
              data-qa={`${paymentType}-submit`}
              className={classnames(
                'btn btn-primary',
                isSubmitting ? `${classes.animatingButton} animating` : classes.animatingButton
              )}
              onClick={async (e) => {
                setIsSubmitting(true);
                const { isValid } = await validate();
                if (isValid) {
                  submit.fn(e, 'createPayment', remainingApprovals);
                } else {
                  setIsSubmitting(false);
                  window.scroll(0, document.querySelector('.has-error') || document.offsetTop);
                }
              }}
              name={submit.label || locale['common.submit']}
            >
              {/* If modify template perform approval check for submit text otherwise handle like before */}
              {action.includes('template') && (
                <>
                  {numOfApprovalsRequiredForTemplate > 0 && (
                    <>{isSubmitting ? 'Submitting For Approval' : 'Submit For Approval'}</>
                  )}
                  {!numOfApprovalsRequiredForTemplate && (
                    <>
                      {isSubmitting
                        ? locale['common.saving']
                        : submit.label || locale['common.submit']}
                    </>
                  )}
                </>
              )}
              {!action.includes('template') && (
                <>
                  {remainingApprovals > 0
                    ? isSubmitting
                      ? locale['common.saving']
                      : submit.label || 'Submit for Approval'
                    : isSubmitting
                    ? locale['common.saving']
                    : submit.label || locale['common.submit']}
                </>
              )}
            </Button>
          )}
          {children({ disabled: submissionDisabled })}
          <Button
            className="btn btn-secondary"
            onClick={cancel.fn}
            variant={SECONDARY}
            type="button"
            data-qa="cancel"
            name={cancel.label || locale['common.cancel']}
          >
            {cancel.label || locale['common.cancel']}
          </Button>
        </div>
        {append && fields.paymentType && append()}
      </div>
    </footer>
  );
}

CreatePaymentFooterComponent.defaultProps = {
  fields: {},
  classes: {},
  locale: {},
  validate: {},
  submit: {},
  cancel: {},
  children() {},
  onChange() {},
  options: {},
  showTotal: false,
  setLoading: true,
  disableOnLoad: true,
  preloadFields: null,
  submissionDisabled: false,
  isSubmitting: false,
  setIsSubmitting: {},
  append: false
};

CreatePaymentFooterComponent.propTypes = {
  onChange: PropTypes.func,
  action: PropTypes.string.isRequired,
  fields: PropTypes.shape({}),
  classes: AppPropTypes.jssClasses,
  locale: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.func,
  cancel: PropTypes.shape({
    fn: PropTypes.func,
    label: PropTypes.string
  }),
  validate: PropTypes.func,
  submit: PropTypes.shape({
    fn: PropTypes.func,
    label: PropTypes.string
  }),
  options: PropTypes.shape({}),
  showTotal: PropTypes.bool,
  setLoading: PropTypes.bool,
  disableOnLoad: PropTypes.bool,
  preloadFields: PropTypes.shape({}),
  submissionDisabled: PropTypes.bool,
  remainingApprovals: PropTypes.number.isRequired,
  isSubmitting: PropTypes.bool,
  setIsSubmitting: PropTypes.func,
  append: PropTypes.bool
};

const Styled = withStyles(styles)(CreatePaymentFooterComponent);
const Context = withFormContext(Styled);
export default connect(
  (state) => ({ remainingApprovals: get(state, 'payments.payment.remainingApprovals', 0) }),
  null
)(withLocale(Context));
